import { useEffect, useState } from "react";
import { useGlobalContext } from "../../GlobalContext";
import axios from "axios";
import { Toaster, toast } from "sonner";
import LoadingScreen from "../Utils/LoadingScreen";
import useShop from "../../Sevices/useShop";

import {
  Store,
  FileText,
  Zap,
  CheckCircle,
  Target,
  BarChart3,
  RefreshCw,
  TrendingUp,
  Hash,
  MapPin,
  Phone,
  CreditCard,
  Building,
  Sparkles,
  Rocket,
  Trash,
  Pen,
  CalendarDays,
  Users,
  Loader2,
  Circle,
} from "lucide-react";

import useDriverAllApi from "../../Sevices/Driver Api/useDriverAllApi";
// ---- OnboardForm (DROP-IN) ----

const Field = ({
  label,
  icon: Icon,
  name,
  value,
  onChange,
  placeholder,
  readOnly = false,
  required = false,
  colSpan = "md:col-span-1",
  type = "text",
}) => (
  <div className={`col-span-1 ${colSpan}`}>
    <label className="admin-input-label block mb-1 text-sm font-medium text-gray-700">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <Icon className="h-4 w-4 text-gray-400" />
      </div>
      <input
        type={type}
        name={name}
        value={value}
        onChange={(e) => onChange(e, name)}
        placeholder={placeholder}
        readOnly={readOnly}
        className={`admin-input pl-9 pr-3 h-11 w-full rounded-xl border bg-white outline-none transition
        ${readOnly ? "" : "focus:ring-2 focus:ring-[#FC981B]"} border-gray-200`}
      />
    </div>
  </div>
);

const OnboardForm = ({
  formData,
  handleInputChange,
  handleFetchLatLong,
  handleAddShop,
}) => {
  return (
    <div className="admin-onboard-card rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="p-5 border-b  rounded-t-2xl">
        <h3 className="text-lg font-semibold text-gray-800">Shop Onboarding</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Fill the details, fetch coordinates, then create the shop.
        </p>
      </div>

      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field
          label="Shop Code"
          icon={Hash}
          name="shopCode"
          value={formData.shopCode}
          onChange={handleInputChange}
          placeholder="e.g., KWLTY-001"
          required
        />

        <Field
          label="Shop Name"
          icon={Store}
          name="shopName"
          value={formData.shopName}
          onChange={handleInputChange}
          placeholder="e.g., Kwality Veg"
          required
        />

        {/* Address with inline Fetch button */}
        <div className="md:col-span-2">
          <label className="admin-input-label block mb-1 text-sm font-medium text-gray-700">
            Address <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MapPin className="h-4 w-4 text-gray-400" />
              </div>
              <input
                name="shopAddress"
                value={formData.shopAddress}
                onChange={(e) => handleInputChange(e, "shopAddress")}
                placeholder="House/Street, Area, City, State, Country, PIN"
                className="admin-input pl-9 pr-3 h-11 w-full rounded-xl border bg-white outline-none transition focus:ring-2 focus:ring-[#FC981B] border-gray-200"
              />
            </div>

            <button
              type="button"
              onClick={handleFetchLatLong}
              className="admin-btn inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-[#2563eb] text-white hover:bg-[#1d4ed8] transition"
              title="Fetch coordinates for the entered address"
            >
              <MapPin className="h-4 w-4" />
              Fetch Lat & Long
            </button>
          </div>
          <p className="text-[11px] text-gray-500 mt-1">
            Tip: Include city, state and PIN for better accuracy.
          </p>
        </div>

        <Field
          label="Phone"
          icon={Phone}
          name="shopPhone"
          value={formData.shopPhone}
          onChange={handleInputChange}
          placeholder="10-digit mobile"
          type="tel"
        />

        <Field
          label="PIN Code"
          icon={Hash}
          name="shopPinCode"
          value={formData.shopPinCode}
          onChange={handleInputChange}
          placeholder="6-digit PIN"
          type="text"
        />

        <Field
          label="PAN"
          icon={CreditCard}
          name="shopPanCard"
          value={formData.shopPanCard}
          onChange={handleInputChange}
          placeholder="e.g., ABCDE1234F"
          colSpan="md:col-span-1"
        />

        <Field
          label="GST"
          icon={Building}
          name="shopGst"
          value={formData.shopGst}
          onChange={handleInputChange}
          placeholder="e.g., 22ABCDE1234F1Z5"
          colSpan="md:col-span-1"
        />

        {/* Lat/Long row side-by-side, read-only */}
        <Field
          label="Latitude (auto-filled)"
          icon={MapPin}
          name="latitude"
          value={formData.latitude}
          onChange={handleInputChange}
          placeholder="Fetch to fill"
          readOnly
        />

        <Field
          label="Longitude (auto-filled)"
          icon={MapPin}
          name="longitude"
          value={formData.longitude}
          onChange={handleInputChange}
          placeholder="Fetch to fill"
          readOnly
        />
      </div>

      {/* Footer actions */}
      <div className="p-5 border-t flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-gray-500">
          Make sure Latitude & Longitude are filled before creating.
        </div>

        <button
          onClick={handleAddShop}
          className={`inline-flex items-center gap-2 h-11 px-6 rounded-xl transition font-medium
            ${formData?.latitude && formData?.longitude
              ? "bg-[#10b981] text-white hover:brightness-95"
              : "bg-gray-200 text-gray-500 cursor-not-allowed"
            }`}
          disabled={!(formData?.latitude && formData?.longitude)}
          title={
            formData?.latitude && formData?.longitude
              ? ""
              : "Fetch Lat & Long first"
          }
        >
          <Sparkles className="h-4 w-4" />
          Create Shop
          <Rocket className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const FormCard = ({
  title,
  buttonText,
  onSubmit,
  formData,
  setFormData,
  className,
  handleFetchLatLong,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleInputChange = (e, key) => {
    setFormData((prev) => ({
      ...prev,
      [key]: e.target.value,
    }));
  };

  const getCardConfig = () => {
    if (title === "Shop Onboarding") {
      return {
        icon: <Store className="text-orange-500" size={32} />,
        description:
          "Welcome new shops to your network! Add all the essential details to get them started on their journey with us.",
        features: [
          {
            icon: <Zap className="w-6 h-6" />,
            title: "Quick Setup",
            desc: "Fast and easy onboarding process",
          },
          {
            icon: <CheckCircle className="w-6 h-6" />,
            title: "Validation",
            desc: "Automatic data verification",
          },
          {
            icon: <Target className="w-6 h-6" />,
            title: "Accuracy",
            desc: "Precise location mapping",
          },
        ],
      };
    } else if (title === "Sync Anlook") {
      return {
        icon: <RefreshCw className="text-blue-500" size={32} />,
        description:
          "Seamlessly integrate and synchronize your shop data with Anlook platform for enhanced business analytics and management.",
        features: [
          {
            icon: <BarChart3 className="w-6 h-6" />,
            title: "Analytics",
            desc: "Advanced business insights",
          },
          {
            icon: <RefreshCw className="w-6 h-6" />,
            title: "Real-time",
            desc: "Instant data synchronization",
          },
          {
            icon: <TrendingUp className="w-6 h-6" />,
            title: "Growth",
            desc: "Track performance metrics",
          },
        ],
      };
    }
    return { icon: null, description: null, features: [] };
  };

  const config = getCardConfig();

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-200px)]">
      <div
        className={`w-full max-w-4xl p-8 border border-gray-300 rounded-lg shadow-sm ${className} ${isVisible ? "card-enter" : "opacity-0"
          }`}
      >
        {config.icon && (
          <div className="text-center mb-8">
            <div
              className={`flex justify-center mb-4 ${isVisible ? "icon-bounce" : "opacity-0"
                }`}
            >
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-md">
                {config.icon}
              </div>
            </div>
            <h2 className="text-3xl font-bold mb-3 text-gray-800">{title}</h2>
            {config.description && (
              <p className="text-gray-600 text-lg mb-6 max-w-2xl mx-auto">
                {config.description}
              </p>
            )}
          </div>
        )}

        <div className="bg-white bg-opacity-60 rounded-lg p-6 mb-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
            <FileText className="mr-2 text-blue-500" size={20} />
            Fill in the Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {Object.keys(formData).map((key) => {
              const isAddress = key === "shopAddress";
              const isLat = key.toLowerCase() === "latitude";
              const isLng = key.toLowerCase() === "longitude";

              return (
                <div
                  key={key}
                  className={`relative ${isAddress ? "md:col-span-2" : ""}`}
                >
                  <input
                    className={`p-3 pl-10 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent bg-white ${isLat || isLng ? "" : ""}`}
                    placeholder={
                      key
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (str) => str.toUpperCase()) +
                      (isLat || isLng ? " (auto-filled)" : "")
                    }
                    value={formData[key]}
                    onChange={(e) => handleInputChange(e, key)}
                    readOnly={isLat || isLng}
                  />

                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    {key.includes("Code") && <Hash size={16} />}
                    {key.includes("Name") && <Store size={16} />}
                    {key.includes("Address") && <MapPin size={16} />}
                    {key.includes("Phone") && <Phone size={16} />}
                    {key.includes("Pan") && <CreditCard size={16} />}
                    {key.includes("Gst") && <Building size={16} />}
                    {(isLat || isLng) && <MapPin size={16} />}
                  </div>

                  {isAddress && (
                    <div className="mt-3 flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={handleFetchLatLong}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        title="Fetch coordinates for the entered address"
                      >
                        <MapPin size={16} />
                        Fetch Lat & Longitude
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="text-center">
          <button
            className={`px-12 py-4 rounded-lg font-semibold text-lg transition-all duration-300 transform shadow-sm flex items-center justify-center mx-auto
    ${formData?.latitude && formData?.longitude
                ? "bg-green-600 text-white hover:scale-105"
                : "bg-gray-300 text-gray-600 cursor-not-allowed"
              }`}
            onClick={onSubmit}
            disabled={!(formData?.latitude && formData?.longitude)}
            title={
              formData?.latitude && formData?.longitude
                ? ""
                : "Fetch Lat & Longitude first"
            }
          >
            <Sparkles className="mr-2" size={20} />
            {buttonText}
            <Rocket className="ml-2" size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default function ShopsPage() {
  const { baseurl, accessToken, token, setIsUnauthorised, AdminId } =
    useGlobalContext();

  const {
    shops,
    loading,
    fetchShops,
    setShops,
    addShop,
    updateShop,
    deleteShop,
    assignTeamToShop,
    fetchLatLong,
    updateShopStatus,
    statusUpdatingId,
  } = useShop({
    baseurl,
    accessToken,
    token,
    AdminId,
    setIsUnauthorised,
  });

  const { teams, fetchDriverTeams } = useDriverAllApi();

  const [activeTab, setActiveTab] = useState("list");
  const [modalType, setModalType] = useState(""); // "assign", "edit", "delete"
  const [selectedShopId, setSelectedShopId] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState("");
  const [filteredShops, setFilteredShops] = useState(null);


  // state
  const [formData, setFormData] = useState({
    shopCode: "",
    shopAddress: "",
    shopName: "",
    shopPhone: "",
    shopPinCode: "",
    shopPanCard: "",
    shopGst: "",
    latitude: "",
    longitude: "",
  });

  // FIX: define the input handler used by OnboardForm & Field
  const handleInputChange = (e, key) => {
    const value = e?.target?.value ?? "";
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const [editFormData, setEditFormData] = useState({
    shopCode: "",
    shopAddress: "",
    shopName: "",
    shopPhone: "",
    shopPinCode: "",
    shopPanCard: "",
    shopGst: "",
    shopLatitude: "",
    shopLongitude: "",
    adminId: AdminId,
  });

  const handleSyncAnlook = async () => {
    try {
      toast.success("Anlook sync completed successfully!");
    } catch (error) {
      toast.error("Error syncing with Anlook");
    }
  };

  const openModal = (type, shopId) => {
    setModalType(type);
    setSelectedShopId(shopId);

    if (type === "assign") {
      const selectedShop = shops.find((s) => s.id === shopId);

      const matchedTeam = (teams || []).find(
        (team) => team.team_name === selectedShop?.teamName,
      );

      setSelectedTeam(matchedTeam ? String(matchedTeam.id) : "");
    }
  };
  const formatDateTime = (value) => {
    if (!value) return "N/A";
    try {
      const date = new Date(value);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return value;
    }
  };

  const InfoBadge = ({ children, color = "gray" }) => {
    const styles = {
      gray: "bg-gray-100 text-gray-700",
      blue: "bg-blue-100 text-blue-700",
      green: "bg-green-100 text-green-700",
      orange: "bg-orange-100 text-orange-700",
      red: "bg-red-100 text-red-700",
      purple: "bg-purple-100 text-purple-700",
    };

    return (
      <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${styles[color]}`}
      >
        {children}
      </span>
    );
  };

  const getShortAddress = (address, len = 60) => {
    if (!address) return "N/A";
    return address.length > len ? address.slice(0, len) + "..." : address;
  };

  const closeModal = () => {
    setModalType("");
    setSelectedShopId(null);
    setSelectedTeam("");
    setEditFormData({
      shopCode: "",
      shopAddress: "",
      shopName: "",
      shopPhone: "",
      shopPinCode: "",
      shopPanCard: "",
      shopGst: "",
      shopLatitude: "",
      shopLongitude: "",
    });
  };

  useEffect(() => {
    fetchShops();
  }, []);

  useEffect(() => {
    fetchDriverTeams();
  }, []);

  useEffect(() => {
    setFilteredShops(shops);
  }, [shops]);

  return (
    <div className="p-6 w-full">
      <Toaster position="top-right" />
      <div className="mb-4 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab("list")}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === "list"
              ? "border-orange-600 text-orange-600"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
          >
            Shop List
          </button>
          <button
            onClick={() => setActiveTab("onboarding")}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === "onboarding"
              ? "border-orange-600 text-orange-600"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
          >
            Onboarding
          </button>
          <button
            onClick={() => setActiveTab("Anlook")}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === "Anlook"
              ? "border-orange-600 text-orange-600"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
          >
            Sync Anlook
          </button>
        </nav>
      </div>

      {loading ? (
        <LoadingScreen />
      ) : (
        <>
          {activeTab === "list" ? (
            <div>
              <div className="flex justify-between items-center mb-4">
                <input
                  type="text"
                  placeholder="Search..."
                  className="px-3 py-1 border rounded w-full max-w-md"
                  onChange={(e) => {
                    const val = e.target.value.toLowerCase();
                    setFilteredShops(
                      shops.filter(
                        (s) =>
                          s.shop_name.toLowerCase().includes(val) ||
                          s.shop_code.toLowerCase().includes(val),
                      ),
                    );
                  }}
                />
                <div className="flex gap-2">
                  <button
                    className="px-3 py-1 bg-green-500 text-white rounded"
                    onClick={() => fetchShops()}
                  >
                    Refresh
                  </button>
                  <button
                    className="px-3 py-1 bg-gray-700 text-white rounded"
                    onClick={() => window.print()}
                  >
                    Export
                  </button>
                </div>
              </div>

              <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-orange-400 text-white border-b border-gray-200">
                      <tr className="text-left">
                        <th className="px-4 py-3 text-xs font-semibold uppercase text-center">#</th>
                        <th className="px-4 py-3 text-xs font-semibold uppercase">Shop</th>
                        <th className="px-4 py-3 text-xs font-semibold uppercase">Contact & Location</th>
                        <th className="px-4 py-3 text-xs font-semibold uppercase">Business</th>
                        <th className="px-4 py-3 text-xs font-semibold uppercase">Team & Status</th>
                        <th className="px-4 py-3 text-xs font-semibold uppercase text-center">Active / Offline</th>
                        <th className="px-4 py-3 text-xs font-semibold uppercase text-center">Actions</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-100">
                      {!filteredShops || filteredShops.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center py-6 text-gray-500">
                            No shops found
                          </td>
                        </tr>
                      ) : (
                        filteredShops.map((shop, index) => (
                          <tr
                            key={shop.id}
                            className="align-top hover:bg-orange-50/40 transition"
                          >
                            {/* Serial */}
                            <td className="px-4 py-4 text-center">
                              <div className="mx-auto flex h-8 w-8 items-center justify-center rounded-full bg-orange-100 text-xs font-bold text-orange-700">
                                {index + 1}
                              </div>
                            </td>

                            {/* Shop */}
                            <td className="px-4 py-3">
                              <div className="min-w-[220px] space-y-2">
                                <div className="flex items-center gap-2">
                                  <Store size={16} className="text-gray-400" />
                                  <p className="text-sm font-semibold text-gray-900">
                                    {shop.shop_name}
                                  </p>
                                </div>

                                <div className="flex flex-wrap gap-1.5">
                                  <InfoBadge color="blue">
                                    <Hash size={11} /> {shop.shop_code}
                                  </InfoBadge>

                                  <InfoBadge color="gray">
                                    ID #{shop.id}
                                  </InfoBadge>
                                </div>

                                <div className="text-xs text-gray-500 flex items-center gap-1">
                                  <CalendarDays size={12} />
                                  {formatDateTime(shop.onboardedOn)}
                                </div>
                              </div>
                            </td>

                            {/* Contact & Location */}
                            <td className="px-4 py-3">
                              <div className="min-w-[240px] space-y-2 text-xs text-gray-700">
                                <div className="flex items-center gap-2">
                                  <Phone size={13} className="text-gray-400" />
                                  {shop.shop_phone || "N/A"}
                                </div>

                                <div className="flex items-start gap-2">
                                  <MapPin size={13} className="text-gray-400 mt-0.5" />
                                  <span>{getShortAddress(shop.shop_address)}</span>
                                </div>

                                <div className="text-gray-500">
                                  PIN: {shop.shop_pincode || "N/A"}
                                </div>
                              </div>
                            </td>

                            {/* Business */}
                            <td className="px-4 py-3">
                              <div className="min-w-[220px] space-y-2 text-xs text-gray-700">
                                <div className="flex items-center gap-2">
                                  <Building size={13} className="text-gray-400" />
                                  GST: {shop.shop_gst || "N/A"}
                                </div>

                                <div className="flex items-center gap-2">
                                  <CreditCard size={13} className="text-gray-400" />
                                  PAN: {shop.shop_pan || "N/A"}
                                </div>

                                <div className="text-gray-500">
                                  Lat: {shop.shop_latitude} <br />
                                  Lng: {shop.shop_longitude}
                                </div>
                              </div>
                            </td>

                            {/* Team & Status */}
                            <td className="px-4 py-3">
                              <div className="min-w-[180px] space-y-2">
                                <InfoBadge color="purple">
                                  <Users size={11} /> {shop.teamName || "Unassigned"}
                                </InfoBadge>

                                <div>
                                  <InfoBadge color={shop.is_active === 1 ? "green" : "red"}>
                                    <span className="flex items-center gap-1">
                                      <Circle size={8} />
                                      {shop.is_active === 1 ? "Online" : "Offline"}
                                    </span>
                                  </InfoBadge>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center">
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateShopStatus({
                                      shopId: shop.id,
                                      status: shop.is_active === 1 ? "0" : "1",
                                    })
                                  }
                                  disabled={statusUpdatingId === shop.id}
                                  className={`relative inline-flex h-7 w-14 items-center rounded-full transition-all duration-300 ${shop.is_active === 1 ? "bg-green-500" : "bg-gray-300"
                                    } ${statusUpdatingId === shop.id ? "opacity-60 cursor-not-allowed" : ""}`}
                                  title={shop.is_active === 1 ? "Click to make offline" : "Click to activate"}
                                >
                                  <span
                                    className={`inline-flex h-5 w-5 transform items-center justify-center rounded-full bg-white shadow-md transition-all duration-300 ${shop.is_active === 1 ? "translate-x-8" : "translate-x-1"
                                      }`}
                                  >
                                    {statusUpdatingId === shop.id ? (
                                      <Loader2 className="h-3 w-3 animate-spin text-gray-500" />
                                    ) : null}
                                  </span>
                                </button>
                              </div>

                              <div className="mt-2 text-center">
                                <span
                                  className={`text-[11px] font-medium ${shop.is_active === 1 ? "text-green-600" : "text-red-500"
                                    }`}
                                >
                                  {shop.is_active === 1 ? "Active" : "Offline"}
                                </span>
                              </div>
                            </td>

                            {/* Actions */}
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => openModal("assign", shop.id)}
                                  className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                                >
                                  Assign
                                </button>

                                <button
                                  onClick={() => openModal("edit", shop.id)}
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  <Pen size={14} />
                                </button>

                                {(AdminId === "3" || AdminId === "13") && (
                                  <button
                                    onClick={() => openModal("delete", shop.id)}
                                    className="text-red-600 hover:text-red-800"
                                  >
                                    <Trash size={14} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : activeTab === "onboarding" ? (
            <OnboardForm
              formData={formData}
              handleInputChange={handleInputChange}
              handleFetchLatLong={async () => {
                const data = await fetchLatLong(formData.shopAddress);
                setFormData((prev) => ({
                  ...prev,
                  latitude: data.latitude,
                  longitude: data.longitude,
                }));
              }}
              handleAddShop={async () => {
                await addShop({
                  ...formData,
                  adminId: AdminId,
                });
                setFormData({
                  shopCode: "",
                  shopAddress: "",
                  shopName: "",
                  shopPhone: "",
                  shopPinCode: "",
                  shopPanCard: "",
                  shopGst: "",
                  latitude: "",
                  longitude: "",
                });
                setActiveTab("list");
              }}
            />
          ) : activeTab === "Anlook" ? (
            <FormCard
              title="Sync Anlook"
              buttonText="Sync Shop"
              onSubmit={handleSyncAnlook}
              formData={formData}
              setFormData={setFormData}
              className="bg-gray-200"
            />
          ) : null}
        </>
      )}
    </div>
  );
}
